import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { Button } from './Button';
import { FormField } from './FormField';
import { TextArea } from './inputs';
import { Modal } from './Modal';

const REASON_MIN_LENGTH = 3;
const REASON_MAX_LENGTH = 240;
const DEFAULT_REASON_LABEL = 'Why are you doing this?';
const DEFAULT_REASON_HINT = 'Recorded with the change, so it can be explained later.';

interface ConfirmDialogCommon {
  /** Whether the dialog is on screen. Defaults to true, so it can simply be rendered conditionally. */
  readonly open?: boolean;
  /** The question, as the heading and the dialog's name: "Remove Jane Okonkwo?". */
  readonly title: string;
  /** What happens if they go ahead. Announced with the dialog, so it is heard before the buttons. */
  readonly consequence: string;
  /** The confirm button, named for the action: "Remove customer", never "OK". */
  readonly confirmLabel: string;
  /** The way out. Defaults to "Cancel". */
  readonly cancelLabel?: string;
  /** The action is in flight: the confirm button stays focusable but refuses another click. */
  readonly busy?: boolean;
  /** Why the action was refused, shown inside the dialog rather than behind it. */
  readonly error?: ReactNode;
  /** Escape, the close control, the scrim and the cancel button all land here. */
  readonly onCancel: () => void;
}

/** Props for a destructive confirmation. */
export interface DestructiveConfirmDialogProps extends ConfirmDialogCommon {
  readonly tier: 'destructive';
  readonly onConfirm: () => void;
}

/** Props for a terminal confirmation, which also collects the reason. */
export interface TerminalConfirmDialogProps extends ConfirmDialogCommon {
  readonly tier: 'terminal';
  /** The reason field's label, phrased as a question: "Why is it being withdrawn?". */
  readonly reasonLabel?: string;
  /** Guidance under the reason field. */
  readonly reasonHint?: string;
  /** Called with the trimmed reason, which is never shorter than three characters. */
  readonly onConfirm: (reason: string) => void;
}

export type ConfirmDialogProps = DestructiveConfirmDialogProps | TerminalConfirmDialogProps;

/**
 * Asks before doing something that cannot be quietly undone.
 *
 * Two tiers, and the difference is who has to be able to answer for it later.
 * `destructive` states the consequence and makes the person confirm by the name
 * of the action. `terminal` also requires a reason, handed to `onConfirm`, for
 * the things an audit trail has to be able to explain: the confirm button stays
 * disabled, and says why, until there is one. Anything reversible should not
 * ask at all; it should act and offer an undo through a toast.
 *
 * Built on {@link Modal}, so it traps focus, closes on Escape and returns focus
 * to whatever opened it. The dialog is stateless about the work itself: pass
 * `busy` while it runs and `error` when it is refused, and it stays open.
 *
 * @param props see {@link ConfirmDialogProps}
 * @returns the dialog, or nothing when `open` is false
 */
export function ConfirmDialog(props: ConfirmDialogProps): React.JSX.Element | null {
  if (props.open === false) {
    return null;
  }
  // A separate component so the typed reason is dropped each time the dialog closes.
  return <ConfirmDialogBody {...props} />;
}

function ConfirmDialogBody(props: ConfirmDialogProps): React.JSX.Element {
  const { title, consequence, confirmLabel, cancelLabel = 'Cancel', busy = false, error } = props;
  const [reason, setReason] = useState('');
  const fieldRef = useRef<HTMLDivElement>(null);
  const missingId = useId();
  const trimmed = reason.trim();
  const ready = props.tier === 'destructive' || trimmed.length >= REASON_MIN_LENGTH;

  // The focus trap starts on the close button. A reason is the one thing the person has to
  // do here, so it takes focus instead. This effect runs after the trap's own.
  useEffect(() => {
    fieldRef.current?.querySelector('textarea')?.focus();
  }, []);

  function confirm(): void {
    if (props.tier === 'terminal') {
      props.onConfirm(trimmed);
    } else {
      props.onConfirm();
    }
  }

  return (
    <Modal
      open
      size="sm"
      title={title}
      description={consequence}
      onClose={props.onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={props.onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={!ready}
            aria-describedby={ready ? undefined : missingId}
            data-confirm-action=""
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {props.tier === 'terminal' ? (
        <div ref={fieldRef}>
          <FormField
            label={props.reasonLabel ?? DEFAULT_REASON_LABEL}
            hint={props.reasonHint ?? DEFAULT_REASON_HINT}
            required
          >
            <TextArea
              rows={3}
              maxLength={REASON_MAX_LENGTH}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </FormField>
          {ready ? null : (
            <p id={missingId} className="fo-confirm__missing">
              Enter a reason of at least {String(REASON_MIN_LENGTH)} characters to continue.
            </p>
          )}
        </div>
      ) : null}
      {error === undefined ? null : (
        <p role="alert" className="fo-confirm__error">
          {error}
        </p>
      )}
    </Modal>
  );
}
